import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  SnackbarContent,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../index";
import {
  serverUrl,
  client,
  firebaseConfig,
  pushNotificationsEnabled,
} from "../config";
import { postNode } from "../core/postNode";
import { useSnackbar } from "notistack";
import { linkGenerator } from "../utils/helpers";

const STORAGE_KEY = `${client}-pushNotificationsEnabled`;
const TOKEN_KEY = `${client}-fcmToken`;

export const getSavedFcmToken = () => localStorage.getItem(TOKEN_KEY);

export const NotificationsHandler = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useEffect(() => {
    let unsubscribe = function () {};

    if (!pushNotificationsEnabled) {
      return;
    }

    if (
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window &&
      messaging
    ) {
      unsubscribe = onMessage(messaging, (remoteMessage) => {

        const { title, body } = remoteMessage.notification || {};
        const { nid, screen, url } = remoteMessage.data || {};

        // Unique key for snackbar
        const key = new Date().getTime() + Math.random();

        enqueueSnackbar("", {
          key,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          persist: true,
          variant: "default",
          content: () => (
            <SnackbarContent
              key={key}
              sx={{
                p: 0,
                maxWidth: 480,
                bgcolor: "background.paper",
                color: "text.primary",
                boxShadow: 3,
                borderRadius: 2,
                flexDirection: "column",
                "& .MuiSnackbarContent-message": {
                  width: "100%",
                  padding: 0,
                },
              }}
              message={
                <Box className="push-notifications-dialog">
                  <Box
                    className="push-notifications-dialog-title"
                    sx={{ paddingTop: "20px", paddingBottom: "20px" }}
                  >
                    {title && (
                      <Typography variant="subtitle1" fontWeight="bold">
                        {title}
                      </Typography>
                    )}
                  </Box>
                  <Box className="push-notifications-dialog-content">
                    {body && <Typography variant="body2">{body}</Typography>}
                  </Box>
                  <Stack
                    className="push-notifications-dialog-actions"
                    justifyContent="flex-end"
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => closeSnackbar(key)}
                    >
                      Dismiss
                    </Button>
                    {(url || screen || nid) && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          let thePath;

                          if (url) {
                            thePath = url;
                          } else {
                            const link = linkGenerator(screen, nid);
                            thePath = link;
                          }

                          closeSnackbar(key);
                          window.location.assign(thePath);
                        }}
                      >
                        View
                      </Button>
                    )}
                  </Stack>
                </Box>
              }
            />
          ),
        });
      });
    }

    return () => unsubscribe();
  }, [enqueueSnackbar, closeSnackbar]);

  return null;
};

const PushNotificationsDialog = ({ open, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(open);

  useEffect(() => {
    const choice = localStorage.getItem(STORAGE_KEY);
    if (choice) {
      setShowDialog(false);
      onClose();
    }
  }, [onClose]);

  const handleAllow = async () => {
    try {
      setLoading(true);
      const permission = await Notification.requestPermission();
      localStorage.setItem(STORAGE_KEY, permission);

      if (permission === "granted") {
        const fbConfig = firebaseConfig();
        const token = await getToken(messaging, {
          vapidKey: fbConfig.vapidKey,
        });

        const payload = {
          token,
          uid: user?.current_user?.uid ?? null,
          topics: ["all"],
          device: "Web",
        };

        postNode(`${serverUrl}/api/fcm/token`, payload)
          .catch((error) => console.log("Failed to store fcm token", error));

        if (token) localStorage.setItem(TOKEN_KEY, token);
      }

      setShowDialog(false);
      onClose();
    } catch (err) {
      console.error("Error enabling notifications:", err);
      setShowDialog(false);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    localStorage.setItem(STORAGE_KEY, "denied");
    setShowDialog(false);
    onClose();
  };

  if (!showDialog || !pushNotificationsEnabled) return null;

  return (
    <Dialog
      className="push-notifications-dialog"
      open={showDialog}
      onClose={handleCancel}
    >
      <DialogTitle className="push-notifications-dialog-title">
        Enable Push Notifications
      </DialogTitle>
      <DialogContent className="push-notifications-dialog-content">
        <DialogContentText
          sx={{ fontSize: "18px" }}
          className="notification-content-container"
        >
          We’d like to send you important updates via push notifications. Please
          click "Allow" to enable this feature.
        </DialogContentText>
      </DialogContent>
      <DialogActions className="push-notifications-dialog-actions">
        <Button onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleAllow} disabled={loading} variant="contained">
          {loading ? "Enabling…" : "Allow"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PushNotificationsDialog;
